import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setWishlistIds(new Set());
      return;
    }
    try {
      const res = await api.getWishlistIds();
      setWishlistIds(new Set(Array.isArray(res?.data) ? res.data : []));
    } catch {
      setWishlistIds(new Set());
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const isWishlisted = (productId) => wishlistIds.has(productId);

  const toggleWishlist = async (productId) => {
    if (!isAuthenticated) {
      throw new Error('LOGIN_REQUIRED');
    }
    if (wishlistIds.has(productId)) {
      await api.removeFromWishlist(productId);
      setWishlistIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      return false;
    }
    await api.addToWishlist(productId);
    setWishlistIds((prev) => new Set(prev).add(productId));
    return true;
  };

  return (
    <WishlistContext.Provider value={{ wishlistIds, isWishlisted, toggleWishlist, refresh, loading }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
}
